import { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";

export default function BeforeAfter() {
  const [pos, setPos] = useState(52);
  const wrapRef = useRef(null);
  const dragging = useRef(false);

  const onMove = (clientX) => {
    if (!wrapRef.current) return;
    const rect = wrapRef.current.getBoundingClientRect();
    const pct = Math.min(100, Math.max(0, ((clientX - rect.left) / rect.width) * 100));
    setPos(pct);
  };

  useEffect(() => {
    const up = () => (dragging.current = false);
    const move = (e) => {
      if (!dragging.current) return;
      const x = e.touches ? e.touches[0].clientX : e.clientX;
      onMove(x);
    };
    window.addEventListener("mouseup", up);
    window.addEventListener("touchend", up);
    window.addEventListener("mousemove", move);
    window.addEventListener("touchmove", move);
    return () => {
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchend", up);
      window.removeEventListener("mousemove", move);
      window.removeEventListener("touchmove", move);
    };
    // Mount-only: dragging is a ref, onMove is stable inside the closure.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section id="compare" className="relative py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-6">
        <div className="max-w-3xl mb-12">
          <p className="text-xs uppercase tracking-[0.25em] text-[#00F0FF] mb-4" data-testid="ba-kicker">Before vs After</p>
          <h2 className="font-display text-4xl sm:text-5xl font-medium text-white leading-tight" data-testid="ba-heading">
            Drag to see the shift.
          </h2>
          <p className="mt-5 text-white/60 text-lg">From chaotic inbox mess to a focused, glowing pipeline.</p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.6 }}
          className="relative glass-card overflow-hidden select-none"
          ref={wrapRef}
          data-testid="before-after-compare"
        >
          <div className="grid grid-cols-1">
            {/* AFTER (full width, base layer) */}
            <div className="p-8 md:p-12 min-h-[420px]">
              <LabelBadge position="right" accent="#00F0FF" text="After Vyomex" testid="ba-label-after" />
              <AfterPanel />
            </div>

            {/* BEFORE (masked overlay) */}
            <div
              className="absolute inset-0 p-8 md:p-12 bg-[#0A0A0A]"
              style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
              aria-hidden="true"
            >
              <LabelBadge position="left" accent="#71717A" text="Before Automation" testid="ba-label-before" />
              <BeforePanel />
            </div>

            {/* Handle */}
            <div
              className="absolute top-0 bottom-0 w-[2px] bg-gradient-to-b from-[#00F0FF] via-[#007AFF] to-[#8B5CF6] shadow-[0_0_24px_rgba(0,240,255,0.8)] cursor-ew-resize"
              style={{ left: `${pos}%` }}
              onMouseDown={() => (dragging.current = true)}
              onTouchStart={() => (dragging.current = true)}
              data-testid="ba-slider"
            >
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black border border-white/20 flex items-center justify-center shadow-[0_0_30px_rgba(0,240,255,0.5)]">
                <div className="flex items-center gap-0.5">
                  <span className="w-0.5 h-4 bg-white/70 rounded" />
                  <span className="w-0.5 h-4 bg-white/70 rounded" />
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function LabelBadge({ position, accent, text, testid }) {
  return (
    <div
      className={`absolute top-6 ${position === "left" ? "left-6" : "right-6"} z-10 px-3 py-1 rounded-full text-xs uppercase tracking-[0.2em] border backdrop-blur-md`}
      style={{ borderColor: `${accent}55`, color: accent, background: "rgba(0,0,0,0.4)" }}
      data-testid={testid}
    >
      {text}
    </div>
  );
}

function BeforePanel() {
  return (
    <div className="ui-messy grid grid-cols-2 gap-3 pt-10">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={`messy-card-${i}`} className="h-16 bg-[#1a1a1a] border border-white/5 rounded-md p-3 opacity-80">
          <div className="h-2 w-2/3 bg-white/20 rounded mb-2" />
          <div className="h-2 w-1/2 bg-white/10 rounded" />
        </div>
      ))}
      <div className="col-span-2 text-center text-white/40 text-sm mt-2 font-mono">
        17 unread · 9 missed follow-ups · 3 lost leads
      </div>
    </div>
  );
}

function AfterPanel() {
  const leads = [
    { name: "Arjun M.", biz: "Gym", status: "Replied", glow: "bg-[#00F0FF]" },
    { name: "Priya K.", biz: "Consultant", status: "Booked", glow: "bg-[#8B5CF6]" },
    { name: "Rahul S.", biz: "Education", status: "Follow-up", glow: "bg-[#007AFF]" },
    { name: "Meera J.", biz: "Clinic", status: "Replied", glow: "bg-[#00F0FF]" },
  ];
  return (
    <div className="pt-10">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {leads.map((l) => (
          <div key={l.name} className="glass-card p-4 flex items-center justify-between">
            <div>
              <div className="text-white font-medium">{l.name}</div>
              <div className="text-white/50 text-xs">{l.biz}</div>
            </div>
            <div className="flex items-center gap-2 text-xs text-white/70">
              <span className={`w-2 h-2 rounded-full ${l.glow} shadow-[0_0_10px_currentColor]`} />
              {l.status}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-6 flex items-center gap-4 text-sm">
        <div className="flex-1 h-1.5 rounded-full bg-white/10 overflow-hidden">
          <div className="h-full w-[82%] progress-shimmer" />
        </div>
        <span className="text-white/70 font-mono text-xs">82% auto-handled</span>
      </div>
    </div>
  );
}
